// ---------------------------------------------------------------------------
// Dashboard page — modernized, scalable card dashboard
// ---------------------------------------------------------------------------
//
// Bespoke composition root (file-manager precedent). Builds the layout, asks the
// registry for the user's card descriptors, loads the persisted layout, then
// hands everything to the layout controller (the single source of truth for
// order/visibility). Tears all of it down on navigation.

import { createElement } from '../core/dom/helpers.js';
import { buildCardDescriptors } from './dashboard/registry.js';
import { createCardGrid } from '../components/card-grid/card-grid-controller.js';
import { isAbortError } from '../core/http/is-abort-error.js';

/**
 * @typedef {import('../contracts/page.js').PageServices & {
 *   dashboardLayoutStore: import('../contracts/card-grid.js').CardGridLayoutStore,
 *   navigate: (path: string) => void,
 * }} DashboardPageServices
 */

/**
 * @param {HTMLElement} container
 * @param {DashboardPageServices} services
 * @returns {import('../contracts/module.js').MountedModule}
 */
export function mountDashboardPage(container, services) {
  const { apiClient, eventBus, logger, t, auth, localeConfig, signal, dashboardLayoutStore, navigate } = services;

  logger.info('Dashboard page mount');

  let destroyed = false;
  /** @type {{ destroy: () => void } | null} */
  let controller = null;

  const heading = createElement('h1', { class: 'dashboard__title' }, ['Dashboard']);
  // Hidden-menu + grid carry the generic card-grid classes (the component styles
  // them); .dashboard__hidden stays for the page-level container layout.
  const hiddenMenu = createElement('div', { class: 'dashboard__hidden card-grid__hidden', role: 'group', 'aria-label': 'Hidden cards' });
  hiddenMenu.hidden = true;
  const grid = createElement('div', { class: 'card-grid' });

  const page = createElement('section', { class: 'dashboard' }, [heading, hiddenMenu, grid]);
  container.appendChild(page);

  /** @type {import('../contracts/dashboard.js').DashboardCardContext} */
  const cardContext = { apiClient, eventBus, logger, t, localeConfig, signal, auth, navigate };

  const descriptors = buildCardDescriptors(cardContext);

  dashboardLayoutStore.load(signal)
    .then((initialLayout) => {
      if (destroyed) return;
      controller = createCardGrid({
        items: descriptors,
        initialLayout,
        layoutStore: dashboardLayoutStore,
        renderItem: (item) => /** @type {any} */ (item).render(cardContext),
        gridContainer: grid,
        hiddenContainer: hiddenMenu,
        labels: { saveErrorToast: 'Couldn’t save dashboard layout' },
        logger,
        eventBus,
        signal,
      });
      logger.info('Dashboard page mounted', { data: { cards: descriptors.length } });
    })
    .catch((/** @type {unknown} */ err) => {
      if (isAbortError(err) || destroyed) return;
      // load() already degrades to a default, so a throw here is unexpected.
      logger.error('Dashboard layout load threw unexpectedly', { data: err });
    });

  return {
    destroy() {
      destroyed = true;
      logger.info('Dashboard page destroy');
      if (controller) { controller.destroy(); controller = null; }
      page.remove();
    },
  };
}

export default mountDashboardPage;
